import type { Dependency, ExpectedProgress, Task, TaskId } from '@bigint-project/core';
import type { CellCoord, ColumnDef } from './types.js';
import { formatCellValue } from './columns.js';
import { formatTaskPredecessorsDisplay } from './dependency-column.js';
import { PROGRESS_STATUS_LABEL } from '../progress/format.js';

/**
 * 儲存格「矩形範圍」選取。
 *
 * 與整列選取(TaskGridProps.selectedTaskIds)是**兩套並存**的選取模型:
 * - 整列選取:跨元件的共享狀態(甘特圖靠它高亮同一批任務),語意是「哪些任務被選中」。
 * - 儲存格範圍:TaskGrid 內部狀態(anchor + focus 兩個座標推導),語意是「複製 / 貼上
 *   的作用區域」,不會單獨送出到上層。
 *
 * 兩者由同一組滑鼠 / 鍵盤操作同時驅動(見 TaskGrid 的 syncRowSelection),因此範圍
 * 涵蓋的列一定也是被選中的列,不會出現「表格看起來選了 5 列、甘特圖只亮 1 列」。
 */
export interface CellRange {
  readonly rowStart: number;
  readonly rowEnd: number;
  readonly colStart: number;
  readonly colEnd: number;
}

/** 由 anchor / focus 兩個座標推導出正規化(start <= end)的矩形範圍。 */
export function normalizeRange(anchor: CellCoord, focus: CellCoord): CellRange {
  return {
    rowStart: Math.min(anchor.rowIndex, focus.rowIndex),
    rowEnd: Math.max(anchor.rowIndex, focus.rowIndex),
    colStart: Math.min(anchor.colIndex, focus.colIndex),
    colEnd: Math.max(anchor.colIndex, focus.colIndex),
  };
}

export function isCellInRange(range: CellRange | null, rowIndex: number, colIndex: number): boolean {
  if (!range) return false;
  return (
    rowIndex >= range.rowStart &&
    rowIndex <= range.rowEnd &&
    colIndex >= range.colStart &&
    colIndex <= range.colEnd
  );
}

/** 範圍是否只涵蓋單一儲存格(單格時不需要畫範圍底色,只留作用格外框)。 */
export function isSingleCell(range: CellRange): boolean {
  return range.rowStart === range.rowEnd && range.colStart === range.colEnd;
}

/**
 * 取得單一儲存格的**顯示值**(格式化後的文字)。
 *
 * 複製到 Excel 的必須是使用者在畫面上看到的字串(例如 `2026-07-24`、`5d`、
 * `NT$1,000`),而不是內部數值(分鐘數、ISO datetime)。
 */
export function cellDisplayText(
  task: Task,
  column: ColumnDef,
  dependencies: readonly Dependency[],
  tasks: readonly Task[],
  resourceNamesByTask?: ReadonlyMap<TaskId, string>,
  expectedProgressByTask?: ReadonlyMap<TaskId, ExpectedProgress>,
): string {
  if (column.kind === 'dependency-list') {
    return formatTaskPredecessorsDisplay(task.id, dependencies, tasks);
  }
  if (column.kind === 'resource-list') {
    return resourceNamesByTask?.get(task.id) ?? '';
  }
  if (column.kind === 'expected-percent') {
    const expected = expectedProgressByTask?.get(task.id);
    return expected === undefined ? '' : `${Math.round(expected.expectedPercent as number)}%`;
  }
  if (column.kind === 'progress-status') {
    const expected = expectedProgressByTask?.get(task.id);
    return expected === undefined ? '' : PROGRESS_STATUS_LABEL[expected.status];
  }
  return formatCellValue(task, column);
}

/**
 * TSV 欄位轉義:含 tab / 換行 / 雙引號的內容用雙引號包起來(Excel 貼上相容),
 * 其餘原樣輸出 —— 否則一個含 tab 的任務名稱會把整列的欄位對齊往後推。
 */
function escapeTsvCell(text: string): string {
  if (!/[\t\r\n"]/.test(text)) return text;
  return `"${text.replace(/"/g, '""')}"`;
}

/** 把矩形範圍轉成可直接貼回 Excel 的 TSV(內容為格式化後的顯示值)。 */
export function buildRangeTsv(
  range: CellRange,
  visibleTasks: readonly Task[],
  columns: readonly ColumnDef[],
  dependencies: readonly Dependency[],
  tasks: readonly Task[],
  resourceNamesByTask?: ReadonlyMap<TaskId, string>,
  expectedProgressByTask?: ReadonlyMap<TaskId, ExpectedProgress>,
): string {
  const lines: string[] = [];
  for (let r = range.rowStart; r <= range.rowEnd; r += 1) {
    const task = visibleTasks[r];
    if (!task) continue;
    const cells: string[] = [];
    for (let c = range.colStart; c <= range.colEnd; c += 1) {
      const column = columns[c];
      if (!column) continue;
      cells.push(escapeTsvCell(cellDisplayText(task, column, dependencies, tasks, resourceNamesByTask, expectedProgressByTask)));
    }
    lines.push(cells.join('\t'));
  }
  return lines.join('\n');
}
